import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@common/bw-ui/lib/utils';

const buttonVariants = cva(
  'tw:inline-flex tw:items-center tw:justify-center tw:gap-2 tw:whitespace-nowrap tw:rounded-md tw:text-sm tw:font-medium tw:transition-colors tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring tw:focus-visible:ring-offset-2 tw:disabled:pointer-events-none tw:disabled:opacity-50 [&_svg]:tw:pointer-events-none [&_svg]:tw:shrink-0',
  {
    variants: {
      variant: {
        default:
          'tw:bg-primary tw:text-primary-foreground tw:hover:bg-primary/90',
        destructive:
          'tw:bg-destructive tw:text-destructive-foreground tw:hover:bg-destructive/90',
        outline:
          'tw:border tw:border-border tw:bg-background tw:hover:bg-secondary tw:hover:text-secondary-foreground',
        'outline-primary':
          'tw:border-2 tw:border-primary tw:bg-background tw:text-primary tw:hover:bg-primary/5',
        'primary-outline':
          'tw:bg-primary tw:text-white tw:border tw:border-primary tw:hover:bg-background tw:hover:text-primary tw:no-underline',
        secondary:
          'tw:bg-secondary tw:text-secondary-foreground tw:hover:bg-secondary/80',
        ghost: 'tw:hover:bg-secondary tw:hover:text-secondary-foreground',
        gray: 'tw:bg-white tw:text-gray-800 tw:no-underline tw:hover:bg-secondary tw:hover:text-secondary-foreground',
        'gray-inverted': 'tw:bg-gray-200 tw:text-black tw:no-underline',
        link: 'tw:text-primary tw:underline-offset-4 tw:hover:underline',
        success:
          'tw:bg-gradient-to-r tw:from-success-gradient-from tw:to-success-gradient-to tw:text-white tw:shadow-md tw:hover:opacity-90',
      },
      size: {
        default: 'tw:h-10 tw:px-4 tw:py-2 [&_svg]:tw:size-4',
        sm: 'tw:h-9 tw:rounded-md tw:px-3 [&_svg]:tw:size-3.5',
        lg: 'tw:h-11 tw:rounded-md tw:px-8 [&_svg]:tw:size-5',
        xl: 'tw:h-14 tw:rounded-md tw:px-6 tw:text-base [&_svg]:tw:size-5',
        icon: 'tw:h-10 tw:w-10 [&_svg]:tw:size-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
